import axios from "axios";
import type { TFunction } from "i18next";

export type AccessRequestErrorAction = "create" | "cancel" | "approve" | "deny";

function readErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const code = (data as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

function readErrorDetail(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const record = data as Record<string, unknown>;
  if (typeof record.detail === "string") return record.detail;

  const nonField = record.non_field_errors;
  if (Array.isArray(nonField)) {
    const first = nonField.find((entry) => typeof entry === "string");
    if (typeof first === "string") return first;
  }

  for (const value of Object.values(record)) {
    if (typeof value === "string") return value;
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  }

  return "";
}

/**
 * Maps access-request API errors to user-facing i18n strings.
 * Returns null when the caller should use a generic fallback message.
 */
export function resolveAccessRequestErrorMessage(
  t: TFunction,
  error: unknown,
  action: AccessRequestErrorAction,
): string | null {
  if (!axios.isAxiosError(error) || error.response == null) {
    return null;
  }

  const status = error.response.status;
  const data = error.response.data;
  const code = readErrorCode(data);
  const detail = readErrorDetail(data);

  if (status === 429) {
    return t("accessRequests.toast.throttled");
  }

  const isDuplicatePending =
    code === "access_request_duplicate" ||
    code === "duplicate_pending_request" ||
    (action === "create" &&
      (status === 409 ||
        (status === 400 && /pending request already exists/i.test(detail))));

  if (isDuplicatePending) {
    return t("accessRequests.toast.duplicatePending");
  }

  const isNotPending =
    (code === "access_request_conflict" && action !== "create") ||
    code === "access_request_not_pending" ||
    (status === 409 && action !== "create");

  if (isNotPending) {
    return t("accessRequests.toast.notPending");
  }

  if (status === 403 && code === "access_request_forbidden") {
    return t("errors.forbidden");
  }

  if (
    status === 403 &&
    (action === "approve" || action === "deny" || action === "cancel") &&
    /household administrators/i.test(detail)
  ) {
    return t("accessRequests.toast.familyCannotDecide");
  }

  return null;
}

/** Inline create-dialog errors: mapped codes first, then API detail, then generic. */
export function accessRequestCreateErrorMessage(error: unknown, t: TFunction): string {
  const mapped = resolveAccessRequestErrorMessage(t, error, "create");
  if (mapped) return mapped;

  if (axios.isAxiosError(error)) {
    const detail = readErrorDetail(error.response?.data);
    if (detail.trim()) return detail;
  }

  return t("accessRequests.toast.createError");
}
